'use client';

import { FocusEvent, useEffect, useRef, useState } from 'react';

import {
    AutoComplete,
    Box,
    BoxContent,
    InputChangeEventWeb,
    ItemSelectedEvent,
    Row,
    Rows,
    Title,
    TitleLevels,
    TrilogyColor,
} from '@bytel/trilogy-react';

import {
    AutocompleteItem,
    CustomerAddress,
    FormData,
    FormFieldName,
    FormFields,
    FormState,
    FormSteps,
    PostalCodeAndCityItem,
} from '@app-types/delivery';

import { localApiPaths } from '@helpers/path';

import { FORM_FIELDS, FORM_STATE } from '@constants/address-form';

import { useDebouncedCallback } from 'use-debounce';

interface GCPProps {
    setAddress: (address: CustomerAddress | null) => void;
}

export function GCPForm({ setAddress }: Readonly<GCPProps>) {
    const [formState, setFormState] = useState<FormState>({ ...FORM_STATE });
    const [formFields, setFormFields] = useState<FormFields>(structuredClone(FORM_FIELDS));

    const debouncedGetPredictions = useDebouncedCallback((name, value) => getAutocompletePrediction(name, value), 250);

    const firstInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => firstInputRef.current?.focus(), []);

    const setAutocompleteData = (fieldName: FormFieldName, data: FormData) => {
        const newFormFields: FormFields = { ...formFields };
        newFormFields[fieldName].data = data;

        setFormFields(newFormFields);
    };

    const setErrorMessage = (fieldName: FormFieldName, message: string) => {
        const newFormFields = { ...formFields };
        newFormFields[fieldName].status = 'error';
        newFormFields[fieldName].errorMessage = message;

        setFormFields(newFormFields);
    };

    const setPostalCodeAutocompleteData = async (postalCode: string) => {
        try {
            const response = await fetch(
                `${localApiPaths.autocompleteCities}?postalCode=${encodeURIComponent(postalCode)}`,
            );
            if (!response.ok) {
                throw new Error('Failed to fetch');
            }
            const autocompleteData = await response.json();

            setAutocompleteData('postalCode', autocompleteData);
        } catch (error) {
            setErrorMessage('postalCode', "Le code postal saisi n'est pas reconnu");
        }
    };

    const setCityAutocompleteData = async (city: string) => {
        try {
            const response = await fetch(`${localApiPaths.autocompleteCities}?city=${encodeURIComponent(city)}`);
            if (!response.ok) {
                throw new Error('Failed to fetch');
            }
            const autocompleteData = await response.json();
            setAutocompleteData('city', autocompleteData);
        } catch (error) {
            setErrorMessage('city', "La ville saisie n'est pas reconnue");
        }
    };

    const setStreetNameAutocompleteData = async (streetName: string) => {
        try {
            const params = new URLSearchParams({
                streetName,
                inseeCode: formState.inseeCode,
                postalCode: formState.postalCode,
            });
            const response = await fetch(`${localApiPaths.autocompleteStreets}?${params}`);
            if (!response.ok) {
                throw new Error('Failed to fetch');
            }
            const autocompleteData = await response.json();
            setAutocompleteData('streetName', autocompleteData);
        } catch (error) {
            setErrorMessage('streetName', "La rue saisie n'est pas reconnue");
        }
    };

    const setStreetNumberAutocompleteData = async (streetNumber: string) => {
        try {
            const params = new URLSearchParams({
                streetNumber,
                streetId: formState.streetId,
                postalCode: formState.postalCode,
            });
            const response = await fetch(`${localApiPaths.autocompleteStreetNumbers}?${params}`);
            if (!response.ok) {
                throw new Error('Failed to fetch');
            }
            const autocompleteData = await response.json();
            setAutocompleteData('streetNumber', autocompleteData);
        } catch (error) {
            setErrorMessage('streetNumber', "Le numéro saisi n'est pas reconnu");
        }
    };

    const handleChange = (e: InputChangeEventWeb) => {
        const inputName = e.inputName as FormFieldName;

        const newFormFields = { ...formFields };
        newFormFields[inputName].value = e.inputValue;
        newFormFields[inputName].isTouched = true;
        newFormFields[inputName].isSelectedFromAutocomplete = false;
        newFormFields[inputName].status = 'default';
        newFormFields[inputName].errorMessage = '';

        setFormFields(newFormFields);

        debouncedGetPredictions(inputName, e.inputValue);
    };

    const getAutocompletePrediction = async (fieldName: FormFieldName, value: string) => {
        if (!value) {
            setAutocompleteData(fieldName, []);
            return;
        }

        switch (fieldName) {
            case 'postalCode':
                await setPostalCodeAutocompleteData(value);
                break;
            case 'city':
                await setCityAutocompleteData(value);
                break;
            case 'streetName':
                await setStreetNameAutocompleteData(value);
                break;
            case 'streetNumber':
                await setStreetNumberAutocompleteData(value);
                break;
            default:
                break;
        }
    };

    const autocompleteSiblingField = (
        newFields: FormFields,
        data: PostalCodeAndCityItem['data'],
        fieldName: FormFieldName,
    ) => {
        if (fieldName === 'postalCode') {
            const cityField: Partial<FormFields['city']> = {
                value: data.city,
                isTouched: true,
                isSelectedFromAutocomplete: true,
                status: 'success',
                errorMessage: '',
            };

            newFields.city = { ...newFields.city, ...cityField };
        } else {
            const postalCodeField: Partial<FormFields['postalCode']> = {
                value: data.postalCode,
                isTouched: true,
                isSelectedFromAutocomplete: true,
                status: 'success',
                errorMessage: '',
            };

            newFields.postalCode = { ...newFields.postalCode, ...postalCodeField };
        }
    };

    const handleBacktracking = (newFormFields: FormFields) => {
        if (formState.step === FormSteps.STREET_NAME) {
            newFormFields.streetName = FORM_FIELDS.streetName;
        } else if (formState.step === FormSteps.STREET_NUMBER) {
            newFormFields.streetName = FORM_FIELDS.streetName;
            newFormFields.streetNumber = FORM_FIELDS.streetNumber;
        }
    };

    const sendAddressDetails = ({ city, postalCode, streetName, streetNumber }: FormState) => {
        const customerAddress: CustomerAddress = {
            city,
            postalCode,
            streetName,
            streetNumber,
            isVerified: true,
        };

        setAddress(customerAddress);
    };

    const updateFormState = (item: AutocompleteItem) => {
        const newFormState = { ...formState };

        switch (item.step) {
            case FormSteps.POSTAL_CODE_CITY:
                newFormState.step = FormSteps.STREET_NAME;

                newFormState.inseeCode = item.data.inseeCode;
                newFormState.postalCode = item.data.postalCode;
                newFormState.city = item.data.city;
                break;

            case FormSteps.STREET_NAME:
                newFormState.step = FormSteps.STREET_NUMBER;

                newFormState.streetId = item.data.streetId;
                newFormState.streetName = item.data.streetName;
                sendAddressDetails(newFormState);
                break;

            case FormSteps.STREET_NUMBER:
                newFormState.streetNumber = item.data.number;

                sendAddressDetails(newFormState);
                break;

            default:
                break;
        }

        setFormState(newFormState);
    };

    const handleItemSelected = (e: ItemSelectedEvent<AutocompleteItem | null>, fieldName: FormFieldName) => {
        if (!e.value) return;

        const newFormFields = { ...formFields };
        newFormFields[fieldName].isSelectedFromAutocomplete = true;
        newFormFields[fieldName].status = 'success';
        newFormFields[fieldName].errorMessage = '';
        newFormFields[fieldName].value = e.value.label;

        if (e.value.step === FormSteps.POSTAL_CODE_CITY) {
            autocompleteSiblingField(newFormFields, e.value.data, fieldName);
        }
        if (formState.step > e.value.step) {
            handleBacktracking(newFormFields);
        }

        setFormFields(newFormFields);
        updateFormState(e.value);
    };

    const handleBlur = (e: unknown, fieldName: FormFieldName) => {
        if (fieldName === 'streetNumber') {
            return;
        }
        const value = (e as FocusEvent<HTMLInputElement>).target.value;

        if (!value) {
            return setErrorMessage(fieldName, 'Veuillez renseigner ce champ');
        }

        if (!formFields[fieldName].isSelectedFromAutocomplete) {
            return setErrorMessage(fieldName, 'Veuillez selectionner un élément de la liste');
        }
    };

    return (
        <Box backgroundColor={TrilogyColor.NEUTRAL_FADE} shadowless>
            <BoxContent>
                <Title level={TitleLevels.FIVE}>Vérifiez votre adresse</Title>
                <Rows gap={1}>
                    <Row>
                        <AutoComplete
                            ref={firstInputRef}
                            name={formFields.postalCode.name}
                            value={formFields.postalCode.value}
                            onChange={handleChange}
                            onItemSelected={(e) => handleItemSelected(e, formFields.postalCode.name)}
                            onBlur={(e) => setTimeout(() => handleBlur(e, formFields.postalCode.name), 200)}
                            placeholder='Code postal'
                            data={formFields.postalCode.data}
                            status={formFields.postalCode.status}
                            help={formFields.postalCode.errorMessage}
                        >
                            {(item) => (
                                <>
                                    <span className='has-text-info'>{item.data.postalCode}</span>
                                    {` ${item.data.city} ${item.data.locality ?? ''}`}
                                </>
                            )}
                        </AutoComplete>
                    </Row>
                    <Row>
                        {' '}
                        <AutoComplete
                            name={formFields.city.name}
                            value={formFields.city.value}
                            onChange={handleChange}
                            onItemSelected={(e) => handleItemSelected(e, formFields.city.name)}
                            placeholder='Ville'
                            data={formFields.city.data}
                            status={formFields.city.status}
                            onBlur={(e) => setTimeout(() => handleBlur(e, formFields.city.name), 200)}
                            help={formFields.city.errorMessage}
                        >
                            {(item) => (
                                <>
                                    {`${item.data.city} ${item.data.locality ?? ''} `}
                                    <span className='has-text-info'>{item.data.postalCode}</span>
                                </>
                            )}
                        </AutoComplete>
                    </Row>
                    {formState.step > FormSteps.POSTAL_CODE_CITY && (
                        <Row>
                            <AutoComplete
                                name={formFields.streetName.name}
                                value={formFields.streetName.value}
                                onChange={handleChange}
                                onItemSelected={(e) => handleItemSelected(e, formFields.streetName.name)}
                                placeholder='Nom de voie'
                                matching={(data) => data} // solution pour que les suggestions s'affichent si on ne met pas "rue", "avenue", etc.
                                data={formFields.streetName.data}
                                status={formFields.streetName.status}
                                onBlur={(e) => setTimeout(() => handleBlur(e, formFields.streetName.name), 200)}
                                help={formFields.streetName.errorMessage}
                            />
                        </Row>
                    )}
                    {formState.step > FormSteps.STREET_NAME && (
                        <Row>
                            <AutoComplete
                                name={formFields.streetNumber.name}
                                value={formFields.streetNumber.value}
                                onChange={handleChange}
                                onItemSelected={(e) => handleItemSelected(e, formFields.streetNumber.name)}
                                placeholder='Numero de voie'
                                data={formFields.streetNumber.data}
                                status={formFields.streetNumber.status}
                                onBlur={(e) => setTimeout(() => handleBlur(e, formFields.streetNumber.name), 200)}
                                help={formFields.streetNumber.errorMessage}
                            />
                        </Row>
                    )}
                </Rows>
            </BoxContent>
        </Box>
    );
}
